/**
 * KOPS-474: Read-Only Buyer Portal ($69 Agent Hub Tier)
 * Conversational MLS search for buyers — no AI chat, no saved searches
 *
 * Reuses: MinimalHeader/Footer from HomePageV3, CompactPropertyCard, PropertyDetailsInline
 * Route: /buyer-portal (routed from DirectAccess based on agent plan)
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faMap, faList, faPhone, faEnvelope,
  faHome, faLock, faArrowUp
} from '@fortawesome/free-solid-svg-icons';
import CompactPropertyCard from '../../components/modules/crm/CompactPropertyCard';
import PropertyDetailsInline from '../../components/modules/crm/PropertyDetailsInline';
import axiosInstance from '../../helpers/api';
import { useMediaQuery } from 'react-responsive';
import '../public-portal/styles/chat-realestate.css';

interface AgentProfile {
  name: string;
  photo: string;
  brokerage: string;
  phone: string;
  email: string;
  license: string;
  logo_url?: string;
  userId?: string;
}

const BuyerPortalReadOnly: React.FC = () => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [properties, setProperties] = useState<any[]>([]);
  const [totalFound, setTotalFound] = useState(0);
  const [lastSearch, setLastSearch] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Selection state
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

  // Agent profile
  const [agentProfile, setAgentProfile] = useState<AgentProfile>({
    name: 'Your Agent',
    photo: 'https://placehold.co/100x100/1e3a8a/ffffff?text=Agent',
    brokerage: '',
    phone: '',
    email: '',
    license: ''
  });

  // Upgrade prompt
  const [showUpgradeHint, setShowUpgradeHint] = useState(false);

  // Load agent profile from URL
  useEffect(() => {
    loadAgentProfile();
  }, []);

  const loadAgentProfile = async () => {
    try {
      // Check localStorage first (set by DirectAccess)
      const storedAgent = localStorage.getItem('portalAgentInfo');
      if (storedAgent) {
        const agent = JSON.parse(storedAgent);
        setAgentProfile({
          name: agent.name || 'Your Agent',
          photo: agent.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.name || 'Agent')}&background=1e3a8a&color=fff`,
          brokerage: agent.brokerage || '',
          phone: agent.phone || '',
          email: agent.email || '',
          license: agent.license || '',
          logo_url: agent.logo_url || null,
          userId: agent.userId || agent.id || ''
        });
      }

      // Also try public API
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const urlParams = new URLSearchParams(window.location.search);
      const agentId = urlParams.get('agentId')
        || localStorage.getItem('portalAgentId')
        || (pathParts.length >= 1 ? pathParts[0] : null);

      if (agentId && agentId.length >= 3) {
        const response = await axiosInstance.get(`/public/agent-profile/${agentId}`);
        if (response.data?.success && response.data?.agent) {
          const a = response.data.agent;
          setAgentProfile(prev => ({
            name: a.name || prev.name,
            photo: a.photo || prev.photo,
            brokerage: a.brokerage || prev.brokerage,
            phone: a.phone || prev.phone,
            email: a.email || prev.email,
            license: a.license || prev.license,
            logo_url: a.logo_url || (prev as any).logo_url,
            userId: agentId
          }));
        }
      }
    } catch (e) {
      console.warn('Could not load agent profile');
    }
  };

  // Auto-trigger search from buyer context
  useEffect(() => {
    const buyerContextStr = localStorage.getItem('portalBuyerContext');
    if (buyerContextStr && !hasSearched) {
      try {
        const ctx = JSON.parse(buyerContextStr);
        if (ctx.searchHistory && ctx.searchHistory.length > 0) {
          const lastSearchQuery = ctx.searchHistory[0].query || ctx.searchHistory[0].searchQuery;
          if (lastSearchQuery) {
            setSearchQuery(lastSearchQuery);
            handleSearch(lastSearchQuery);
          }
        }
      } catch (e) { /* ignore */ }
    }
  }, []);

  const handleSearch = async (query?: string) => {
    const searchText = query || searchQuery;
    if (!searchText.trim()) return;

    setSearchLoading(true);
    setLastSearch(searchText);
    setHasSearched(true);
    setSelectedProperty(null);

    try {
      const response = await axiosInstance.post('/theta-ai/search', {
        query: searchText,
        sessionId: `buyer-portal-${Date.now()}`,
        userId: localStorage.getItem('portalLeadId') || 'anonymous',
        includeAllResults: true
      });

      if (response.data?.properties) {
        const mapped = response.data.properties.map((p: any) => ({
          ...p,
          id: p.id || p.mls_number || p.mlsNumber,
          mls_number: p.mls_number || p.mlsNumber || p.id,
          address: typeof p.address === 'object' ? p.address.street : (p.address || p.street_address || `${p.streetNumber || ''} ${p.streetName || ''}`.trim() || 'Address not available'),
          city: p.address?.city || p.city || '',
          state: p.address?.state || p.state || 'AZ',
          zip: p.address?.zip || p.zip || p.zipCode || p.postal_code || '',
          price: p.price || p.list_price || p.listPrice || 0,
          bedrooms: p.bedrooms || p.beds || p.bedroomsTotal || 0,
          bathrooms: p.bathrooms || p.baths || p.bathroomsTotalDecimal || 0,
          square_feet: p.square_feet || p.sqft || p.squareFeet || p.buildingAreaTotal || 0,
          latitude: p.latitude || p.lat || p.coordinates?.latitude,
          longitude: p.longitude || p.lng || p.coordinates?.longitude,
          images: p.images || p.photos || p.media?.photos || [],
          listingBrokerage: p.listingBrokerage || p.listing_brokerage || p.listingOfficeName || 'MLS Partner',
          listingAgent: p.listingAgent || p.listing_agent || p.listingAgentName || '',
          description: p.description || p.remarks || p.publicRemarks || '',
          year_built: p.year_built || p.yearBuilt || null,
          pool: p.pool || false,
          garage: p.garage || 0,
          matchScore: p.matchScore || p.match_score || p.score || 75
        }));

        setProperties(mapped);
        setTotalFound(response.data.totalFound || mapped.length);

        // Show upgrade hint once after first search
        if (!showUpgradeHint) {
          setTimeout(() => setShowUpgradeHint(true), 3000);
        }
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectProperty = async (property: any) => {
    setSelectedProperty(property);
    if (isMobile) setMobileView('detail');

    // Fetch full details if needed
    if (!property.description && property.id) {
      setDetailLoading(true);
      try {
        const response = await axiosInstance.get(`/properties/${property.id}`);
        if (response.data) {
          setSelectedProperty({ ...property, ...response.data });
        }
      } catch (e) { /* use what we have */ }
      finally { setDetailLoading(false); }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  // ─── Render ──────────────────────────────────────────────

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* ─── Header (same as HomePageV3 MinimalHeader) ──────── */}
      <header className="minimal-header border-bottom" style={{ background: 'white', position: 'sticky', top: 0, zIndex: 1000 }}>
        <Container fluid className="py-2">
          <Row className="align-items-center">
            {agentProfile.brokerage && (
              <Col xs="auto" className="d-flex align-items-center me-3">
                {(agentProfile as any).logo_url ? (
                  <img
                    src={(agentProfile as any).logo_url}
                    alt={agentProfile.brokerage}
                    style={{ height: '40px', maxWidth: '160px', objectFit: 'contain' }}
                  />
                ) : (
                  <div>
                    <div className="fw-semibold" style={{ fontSize: '1rem' }}>{agentProfile.brokerage}</div>
                  </div>
                )}
              </Col>
            )}
            <Col>
              <div className="d-flex align-items-center">
                <h5 className="mb-0 me-3">AI Search Portal</h5>
                <Badge bg="primary" className="me-2">Conversational Search</Badge>
                <small className="text-muted d-none d-md-inline">Search properties using everyday language</small>
              </div>
            </Col>
            {agentProfile.name !== 'Your Agent' && (
              <Col xs="auto" className="d-flex align-items-center">
                <img
                  src={agentProfile.photo}
                  alt={agentProfile.name}
                  className="rounded-circle me-2"
                  style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                />
                <div className="me-3">
                  <div className="fw-bold small">{agentProfile.name}</div>
                  {agentProfile.brokerage && (
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{agentProfile.brokerage}</div>
                  )}
                </div>
                {agentProfile.phone && (
                  <Button
                    variant="primary"
                    size="sm"
                    href={`tel:${agentProfile.phone}`}
                  >
                    Contact Agent
                  </Button>
                )}
              </Col>
            )}
          </Row>
        </Container>
      </header>

      {/* ─── Main Content (Split Panel) ─────────────────────── */}
      <div className="flex-grow-1 d-flex" style={{ overflow: 'hidden' }}>

        {/* Left Panel — Search + Results */}
        <div style={{
          width: isMobile ? '100%' : '420px',
          minWidth: isMobile ? '100%' : '420px',
          borderRight: '1px solid #dee2e6',
          display: 'flex',
          flexDirection: 'column',
          background: '#f8f9fa',
          ...(isMobile && mobileView === 'detail' ? { display: 'none' } : {})
        }}>
          {/* Search Bar */}
          <div style={{ padding: '16px', borderBottom: '1px solid #dee2e6', background: 'white' }}>
            <div style={{ position: 'relative' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '12px', top: '12px', color: '#6c757d' }} />
              <input
                type="text"
                placeholder="Try: 3 bed in Gilbert under 400K with a pool"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyPress}
                style={{
                  width: '100%',
                  padding: '10px 10px 10px 38px',
                  border: '2px solid #dee2e6',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={e => e.target.style.borderColor = '#0d6efd'}
                onBlur={e => e.target.style.borderColor = '#dee2e6'}
              />
            </div>
            <Button
              onClick={() => handleSearch()}
              disabled={searchLoading || !searchQuery.trim()}
              className="w-100 mt-2"
              variant="primary"
            >
              {searchLoading ? <Spinner size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSearch} className="me-1" />}
              Search Properties
            </Button>

            {/* Suggested Searches (before first search) */}
            {!hasSearched && (
              <div className="mt-3">
                <small className="text-muted fw-semibold">Try searching for:</small>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {[
                    '3 bed 2 bath in Gilbert under 400K',
                    'Pool homes in Chandler',
                    'New construction Mesa',
                    'Single story in Tempe',
                    'Homes near good schools'
                  ].map((suggestion, i) => (
                    <Button key={i} size="sm" variant="outline-secondary"
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                      onClick={() => { setSearchQuery(suggestion); handleSearch(suggestion); }}>
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Result Count + View Toggle */}
          {hasSearched && (
            <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ borderBottom: '1px solid #dee2e6', background: 'white' }}>
              <small className="text-muted fw-semibold">
                {searchLoading ? 'Searching...' : `Found ${totalFound} properties`}
              </small>
              <div className="d-flex gap-1">
                <Button size="sm" variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                  onClick={() => setViewMode('list')} style={{ padding: '2px 8px' }}>
                  <FontAwesomeIcon icon={faList} />
                </Button>
                <Button size="sm" variant={viewMode === 'map' ? 'primary' : 'outline-secondary'}
                  onClick={() => setViewMode('map')} style={{ padding: '2px 8px' }}>
                  <FontAwesomeIcon icon={faMap} />
                </Button>
              </div>
            </div>
          )}

          {/* Property Cards */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {searchLoading && (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-2">Searching properties...</p>
              </div>
            )}

            {!searchLoading && !hasSearched && (
              <div className="text-center py-5">
                <FontAwesomeIcon icon={faHome} style={{ fontSize: '48px', color: '#dee2e6', marginBottom: '16px' }} />
                <p className="text-muted">Search for properties using everyday language</p>
                <p className="text-muted" style={{ fontSize: '13px' }}>
                  Example: "3 bedroom house in Gilbert with a pool under 500K"
                </p>
              </div>
            )}

            {!searchLoading && hasSearched && properties.length === 0 && (
              <div className="text-center py-5">
                <p className="text-muted">No properties found. Try a different search.</p>
              </div>
            )}

            {!searchLoading && properties.map((property, index) => (
              <div
                key={property.id || index}
                onClick={() => handleSelectProperty(property)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  border: selectedProperty?.id === property.id ? '2px solid #0d6efd' : '1px solid #dee2e6',
                  overflow: 'hidden'
                }}
              >
                <CompactPropertyCard
                  property={property}
                  onView={() => handleSelectProperty(property)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel — Property Detail */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          background: 'white',
          ...(isMobile && mobileView === 'list' ? { display: 'none' } : {})
        }}>
          {/* Mobile back button */}
          {isMobile && mobileView === 'detail' && (
            <Button variant="link" onClick={() => setMobileView('list')} className="m-2">
              ← Back to results
            </Button>
          )}

          {!selectedProperty ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
              <div className="text-center" style={{ maxWidth: '400px', padding: '40px' }}>
                <FontAwesomeIcon icon={faHome} style={{ fontSize: '64px', color: '#dee2e6', marginBottom: '20px' }} />
                <h5 className="text-muted mb-3">
                  {hasSearched ? 'Select a property to view details' : 'Search for your dream home'}
                </h5>
                <p className="text-muted" style={{ fontSize: '14px' }}>
                  {hasSearched
                    ? 'Click any property on the left to see photos, details, and how to schedule a showing.'
                    : 'Use conversational search to find properties. Just describe what you\'re looking for in your own words.'
                  }
                </p>

                {/* Upgrade hint (shown once, subtly) */}
                {showUpgradeHint && (
                  <div className="mt-4 p-3 rounded" style={{ background: '#f0f4ff', border: '1px solid #c7d6ff' }}>
                    <FontAwesomeIcon icon={faLock} className="text-primary me-2" />
                    <small className="text-primary fw-semibold">AI Search Available</small>
                    <p style={{ fontSize: '12px', color: '#555', margin: '8px 0 0' }}>
                      Want AI-powered conversational search with personalized recommendations? Ask your agent about upgrading to AI Search.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : detailLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div>
              {/* Property Detail */}
              <PropertyDetailsInline
                property={selectedProperty}
                isExpanded={true}
                onToggleExpand={() => { setSelectedProperty(null); if (isMobile) setMobileView('list'); }}
              />

              {/* Agent Contact Card */}
              <div className="p-4">
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid #dee2e6',
                  borderLeft: '4px solid #0d6efd'
                }}>
                  <div className="d-flex align-items-center mb-3">
                    <img
                      src={agentProfile.photo}
                      alt={agentProfile.name}
                      className="rounded-circle me-3"
                      style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                    />
                    <div>
                      <div className="fw-bold">{agentProfile.name}</div>
                      {agentProfile.brokerage && (
                        <div className="text-muted" style={{ fontSize: '0.85rem' }}>{agentProfile.brokerage}</div>
                      )}
                      {agentProfile.license && (
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>License: {agentProfile.license}</div>
                      )}
                    </div>
                  </div>
                  <p style={{ fontSize: '14px', color: '#555', marginBottom: '12px' }}>
                    Interested in this property? Contact me to schedule a showing or get more information.
                  </p>
                  <div className="d-flex gap-2 flex-wrap">
                    {agentProfile.phone && (
                      <Button variant="primary" size="sm" href={`tel:${agentProfile.phone}`}>
                        <FontAwesomeIcon icon={faPhone} className="me-1" /> {agentProfile.phone}
                      </Button>
                    )}
                    {agentProfile.email && (
                      <Button variant="outline-primary" size="sm" href={`mailto:${agentProfile.email}?subject=Inquiry about ${selectedProperty.address || 'a property'}`}>
                        <FontAwesomeIcon icon={faEnvelope} className="me-1" /> Email Agent
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Footer (same as HomePageV3) ────────────────────── */}
      <footer className="border-top mt-auto py-3" style={{ background: '#f8f9fa' }}>
        <Container fluid>
          <Row className="align-items-center justify-content-center text-center">
            <Col xs="auto">
              <img
                src={require('../../assets/img/logos/equal-housing-opportunity-logo-1200w.jpg')}
                alt="Equal Housing Opportunity"
                style={{ height: '30px', objectFit: 'contain' }}
              />
            </Col>
            <Col xs="auto">
              <small className="text-muted">
                Equal Housing Opportunity. All information deemed reliable but not guaranteed.
              </small>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default BuyerPortalReadOnly;
